import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentProject } from '@/shared/hooks/useCurrentProject';
import { BarChart3, ChevronDown, Download, MoreHorizontal, Plus } from 'lucide-react';
import { BaseLayout } from '@/shared/components/layout';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { PaginationControls, usePagination, paginateData } from '@/shared/components/pagination';
import type { SubmittalFilters, SubmittalSortState, SubmittalSummary } from '../types';
import { SUBMITTAL_STATUS_LABELS } from '../types';
import { SubmittalFormDialog } from './SubmittalFormDialog';
import { SubmittalsTable } from './SubmittalsTable';
import { SubmittalsFilters } from './SubmittalsFilters';
import { SubmittalsDashboard } from './SubmittalsDashboard';
import {
  useSubmittal,
  useSubmittals,
  useSubmittalMutations,
  useSubmittalStatusSummary,
} from '../hooks';
import { getSubmittalsExportCsvUrl } from '../api/api';
import { SUBMITTALS_LIST_TOUR_ID } from '@/features/tours/submittals/list';
import { SUBMITTALS_COMBINED_TOUR_ID } from '@/features/tours/submittals/combined';
import '@/features/tours/submittals/detail';
import { useTour } from '@/features/tours';
import { sortListItemsByRules } from '@/shared/utils/listSorting';
import { handleApiError } from '@/shared/api/requests';
import { useToast } from '@/shared/hooks/use-toast';

const DEFAULT_FILTERS: SubmittalFilters = { current_revision_only: true };

export const SubmittalsPage: React.FC = () => {
  const { projectIdNumber: numericProjectId } = useCurrentProject();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [filters, setFilters] = useState<SubmittalFilters>(DEFAULT_FILTERS);
  const [sort, setSort] = useState<SubmittalSortState[]>([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | undefined>();
  const [summaryExpandedOverride, setSummaryExpandedOverride] = useState<boolean | null>(null);
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 639px)');
    const updateViewport = () => setIsMobileViewport(mediaQuery.matches);
    updateViewport();
    mediaQuery.addEventListener('change', updateViewport);
    return () => mediaQuery.removeEventListener('change', updateViewport);
  }, []);

  // The same React-Query call ``SubmittalsTable`` makes is reused here; deduped.
  const { data: items, isLoading, error } = useSubmittals(numericProjectId, filters);
  const { data: editingItem } = useSubmittal(editingItemId, numericProjectId);
  const { data: statusSummary } = useSubmittalStatusSummary(numericProjectId);
  const { deleteItem } = useSubmittalMutations(numericProjectId);

  const allItems = useMemo(() => items ?? [], [items]);
  const sortedItems = useMemo(() => sortSubmittalItems(allItems, sort), [allItems, sort]);
  const totalCount = useMemo(
    () => (statusSummary ?? []).reduce((sum, entry) => sum + entry.count, 0),
    [statusSummary]
  );
  const filteredCount = allItems.length;

  const [paginationState, paginationActions, paginationInfo] = usePagination(filteredCount, 10);
  const pagedItems = useMemo(
    () => paginateData(sortedItems, paginationState.currentPage, paginationState.itemsPerPage),
    [sortedItems, paginationState.currentPage, paginationState.itemsPerPage]
  );

  const handleFiltersChange = (next: SubmittalFilters) => {
    setFilters(next);
    paginationActions.setCurrentPage(1);
  };

  const handleSortChange = (next: SubmittalSortState[]) => {
    setSort(next);
    paginationActions.setCurrentPage(1);
  };

  const { hydrated: tourHydrated, active, startTour, hasSeen } = useTour();
  const hasItems = allItems.length >= 1;
  const isSummaryVisible = summaryExpandedOverride ?? !isMobileViewport;
  useEffect(() => {
    if (!tourHydrated) return;
    if (active) return;
    if (hasItems) {
      if (hasSeen(SUBMITTALS_COMBINED_TOUR_ID)) return;
      if (hasSeen(SUBMITTALS_LIST_TOUR_ID)) return;
      void startTour(SUBMITTALS_COMBINED_TOUR_ID);
    } else {
      if (hasSeen(SUBMITTALS_LIST_TOUR_ID)) return;
      void startTour(SUBMITTALS_LIST_TOUR_ID);
    }
  }, [tourHydrated, active, startTour, hasSeen, hasItems]);

  const handleRowClick = (item: SubmittalSummary) => {
    navigate(item.id);
  };

  const handleDelete = async (item: SubmittalSummary) => {
    if (!window.confirm(`Delete ${item.submittal_number} “${item.title}”? This cannot be undone.`)) return;
    try {
      await deleteItem.mutateAsync(item.id);
      toast({ title: 'Submittal deleted' });
    } catch (deleteError) {
      toast({
        title: 'Unable to delete submittal',
        description: handleApiError(deleteError),
        variant: 'destructive',
      });
    }
  };

  return (
    <BaseLayout>
      <div className="min-w-0 space-y-6 p-4 md:p-6">
        {/* Page header */}
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:gap-x-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight">Submittals</h1>
            <p className="mt-1 text-sm text-muted-foreground">Review and track approvals.</p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="-ml-3 mt-1 h-8 gap-2 px-3 text-muted-foreground hover:text-foreground"
              aria-expanded={isSummaryVisible}
              aria-controls="submittals-summary"
              data-tour-id="submittals-summary-toggle"
              onClick={() =>
                setSummaryExpandedOverride((current) => !(current ?? !isMobileViewport))
              }
            >
              <BarChart3 className="h-4 w-4" />
              {isSummaryVisible ? 'Hide summary' : 'Show summary'}
              <ChevronDown
                className={`h-4 w-4 transition-transform ${isSummaryVisible ? 'rotate-180' : ''}`}
              />
            </Button>
          </div>
          <div
            className={`grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:items-center ${
              isSummaryVisible ? 'order-2 sm:order-none' : ''
            }`}
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                  disabled={!numericProjectId}
                >
                  <MoreHorizontal className="mr-2 h-4 w-4" />
                  Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 max-w-[calc(100vw-2rem)]">
                {numericProjectId && (
                  <DropdownMenuItem asChild>
                    <a
                      href={getSubmittalsExportCsvUrl(numericProjectId)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Export CSV
                    </a>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              size="sm"
              className="w-full sm:w-auto"
              onClick={() => setIsCreateOpen(true)}
              disabled={!numericProjectId}
              data-tour-id="submittals-new"
            >
              <Plus className="mr-2 h-4 w-4" />
              New Submittal
            </Button>
          </div>

          {isSummaryVisible && (
            <div
              id="submittals-summary"
              className="order-1 min-w-0 sm:order-none sm:col-span-2 sm:mt-3"
            >
              <SubmittalsDashboard projectId={numericProjectId} />
            </div>
          )}
        </div>

        <SubmittalsFilters
          projectId={numericProjectId}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          totalCount={totalCount}
          filteredCount={filteredCount}
          sort={sort}
          onSortChange={handleSortChange}
        />

        <SubmittalsTable
          items={pagedItems}
          isLoading={isLoading}
          error={error}
          onRowClick={handleRowClick}
          onEdit={(item) => setEditingItemId(item.id)}
          onDelete={(item) => void handleDelete(item)}
        />

        {!isLoading && !error && filteredCount > 0 && (
          <PaginationControls
            state={paginationState}
            actions={paginationActions}
            info={paginationInfo}
          />
        )}
      </div>

      <SubmittalFormDialog
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        projectId={numericProjectId}
      />
      <SubmittalFormDialog
        open={!!editingItemId && !!editingItem}
        onClose={() => setEditingItemId(undefined)}
        projectId={numericProjectId}
        item={editingItem}
      />
    </BaseLayout>
  );
};

const SUBMITTAL_PRIORITY_ORDER = { LOW: 0, MEDIUM: 1, HIGH: 2, CRITICAL: 3 } as const;

export const sortSubmittalItems = (
  items: SubmittalSummary[],
  sort: readonly SubmittalSortState[]
) => {
  return sortListItemsByRules(items, sort, (item, key) => {
    switch (key) {
      case 'submittal_number':
        return item.submittal_number;
      case 'status':
        return SUBMITTAL_STATUS_LABELS[item.status];
      case 'priority':
        return SUBMITTAL_PRIORITY_ORDER[item.priority];
      case 'reviewer':
        return item.current_reviewer
          ? `${item.current_reviewer.first_name} ${item.current_reviewer.last_name}`
          : null;
      case 'required_response_date':
        return item.required_response_date ? Date.parse(item.required_response_date) : null;
      case 'days_open':
        return item.days_open;
    }
  });
};
